import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRoster } from '../lib/roster'
import { useAuth } from '../lib/session'
import { supabase } from '../lib/supabase'
import { TAB_LABEL, isTabKey } from '../lib/filters'
import { insertAtCursor } from '../lib/templates'
import { isGuidanceTemplate, type EmailTemplate } from '../lib/types'
import {
  LANE_FROM,
  MERGE_FIELDS,
  REPLY_TO,
  blastAudienceRule,
  canSend,
  gmailComposeUrl,
  gmailLinkTooLong,
  localDate,
  mergeFieldsUsed,
  problemsWith,
  render,
  resolveAudience,
  type Lane,
} from '../lib/campaigns'
import { MANUAL_TRIGGER, batchKey, markDraft, queueBlast, queueReviewed, writeHandedOff } from '../lib/outbox'
import {
  DAY_NAMES,
  DEFAULT_CADENCE_STATE,
  WEEK_LABELS,
  buildCadence,
  cadenceSummary,
  type CadencePickerState,
  type CadenceType,
} from '../lib/cadence-picker'
import { validateCadence } from '../lib/cadence'

/**
 * Writing an email to a group of agents.
 *
 * Four questions, in this order, and the order is load-bearing:
 *
 *   WHO      taken from the mailing list, or from whatever the filters show
 *   HOW      read each one, or the same message to everybody
 *   WHAT     a template or a blank page
 *   CONFIRM  with the number said out loud
 *
 * **HOW comes before WHAT on purpose.** It decides what is legal to write: the
 * reviewed lane may use fill-ins, a blast may not. Asked afterwards, she could
 * write a personalised email and only then be told she cannot send it that way.
 * Asked first, the composer simply never offers the fill-ins.
 *
 * Nothing here sends. Both endings put something in front of her that waits.
 */

/**
 * Four steps for a recurring campaign, three for everything else. `who` was
 * folded into `how` — see the comment on that branch. `cadence` sits between
 * `what` and `confirm`, and only exists on the path where `repeat` is true —
 * see the notes on that state below for why a repeating send needs a step a
 * one-time send does not.
 */
type Step = 'how' | 'what' | 'cadence' | 'confirm'

/**
 * Nothing is composed until the roster is actually here.
 *
 * `picked` below is frozen the moment the composer mounts, and `/email?tab=…`
 * is a real address now — bookmarkable, reloadable, reachable with the Back
 * button. Reloading it ran that snapshot before the 785 agents had arrived, so
 * the first screen said "0 agents" under the heading "Everyone the Team Leaders
 * list is currently showing", with Continue permanently greyed out and nothing
 * on screen suggesting that waiting or reloading would help.
 *
 * THE TAGS COUNT AS "THE ROSTER" HERE TOO. A tag filter is answered against
 * `tagsByAgent`, so opening the composer in the gap between the agents arriving
 * and the tags arriving froze the list at nobody — the same permanent zero,
 * reached by a different route. Both fetches have to have answered.
 *
 * A separate component rather than an early return, because the freeze has to
 * happen on a mount that already has the data — the hook cannot be skipped.
 */
export default function Email() {
  const { agents, error, reloadAgents, tagsLoaded } = useRoster()
  if (error && !agents) {
    return (
      <div className="board tpl">
        <section className="sec">
          <p className="error">The agent list did not load, so there is nobody to write to. {error}</p>
          <div className="savebar">
            <button className="btn" onClick={() => reloadAgents()}>Try again</button>
            <button className="btn ghost" onClick={() => window.history.back()}>Back</button>
          </div>
        </section>
      </div>
    )
  }
  if (!agents || !tagsLoaded) return <div className="empty">Loading…</div>
  return <Compose />
}

function Compose() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const {
    agents,
    visible,
    selection,
    tagsByAgent,
    tagsError,
    tagsLoading,
    reloadTags,
    clearSelection,
    filters,
    search,
    includeDeparted,
  } = useRoster()
  const { appUser } = useAuth()

  /**
   * Which list she pressed Email from.
   *
   * Carried in the URL rather than read from the roster, because the roster
   * does not know which tab is on screen — Export, Tag and Views are all told
   * the same way. A URL can say anything, so it is checked against the real
   * tabs and falls back to All.
   */
  const fromTab = (() => {
    const t = params.get('tab')
    return isTabKey(t) ? t : 'all'
  })()

  const [step, setStep] = useState<Step>('how')
  const [lane, setLane] = useState<Lane | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [label, setLabel] = useState('')
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  /**
   * Set only by the single-recipient Gmail shortcut below, once the Gmail
   * window has actually opened AND the one `outreach_drafts` row behind it is
   * confirmed written — never on a guess. Its presence is what switches the
   * screen below from the ordinary "Done" message to the "It went" screen;
   * `done` is never set on this path (see the `usedBypass` branch in
   * `submit`), so the two are mutually exclusive by construction, not by a
   * priority check here.
   */
  const [gmailHandoff, setGmailHandoff] = useState<{
    id: number
    agentId: string
    occurrence: string
    to: string
  } | null>(null)
  const [handoffBusy, setHandoffBusy] = useState(false)
  const [handoffDone, setHandoffDone] = useState(false)
  const [handoffError, setHandoffError] = useState<string | null>(null)

  /**
   * Whether this is being built as a recurring campaign rather than a one-time
   * send. Only reachable from the blast lane, and only when NOT sending to a
   * hand-ticked mailing list — see the button that sets this, in the HOW step.
   * A recurring campaign's audience has to be a live filter (re-resolved at
   * every firing), and a ticked list is a frozen snapshot by construction; it
   * has no way to notice a new joiner, which is the one thing a recurring
   * group must do. `scheduled-audience.ts`'s own header makes the same point.
   */
  const [repeat, setRepeat] = useState(false)
  const [cadenceState, setCadenceState] = useState<CadencePickerState>(DEFAULT_CADENCE_STATE)
  const [fireTime, setFireTime] = useState('09:00')

  /**
   * Who, frozen the moment this screen opened.
   *
   * The roster keeps moving — the licence filters are answered against today's
   * date and the clock is watched — so re-reading it at each step could change
   * the list under a number she has already been shown. What she confirms has
   * to be what she was counting.
   */
  const [picked] = useState(() => {
    const all = agents ?? []
    if (selection.size > 0) return all.filter((a) => selection.has(a.rezen_id))
    return visible(fromTab)
  })
  const fromMailingList = selection.size > 0

  useEffect(() => {
    supabase
      .from('email_templates')
      .select('*')
      .order('name')
      .then(({ data }) => setTemplates((data ?? []) as EmailTemplate[]))
  }, [])

  /**
   * `lane` is passed through as null until she has chosen one.
   *
   * It used to resolve under the blast rule while the WHO screen was up, so a
   * `no-bulk-email` agent was subtracted from a count that then turned out to
   * be a reviewed send and gained them back. The number on the first screen
   * disagreed with the number on the last one and nothing explained the two.
   */
  const audience = useMemo(
    () => resolveAudience(picked, lane, tagsByAgent, body, subject),
    [picked, lane, tagsByAgent, body, subject],
  )
  const problems = useMemo(
    () => (lane ? problemsWith({ lane, subject, body, audience }) : []),
    [lane, subject, body, audience],
  )
  /**
   * Nothing may be queued while the tags are unknown.
   *
   * `no-outreach` and `no-bulk-email` live in `agent_tags`. If that table did
   * not load, every one of them is silently absent and this screen would
   * cheerfully queue an email to somebody who asked not to get one, with no
   * symptom anywhere. Refusing is the only honest answer.
   */
  const ready = canSend(problems) && !tagsError && lane !== null

  /**
   * Whether the confirm screen should describe the single-recipient Gmail
   * shortcut rather than the ordinary "goes in the Outbox" copy — display
   * only, computed the same way `submit()` decides whether to actually take
   * the shortcut, minus the parts that only make sense at send time (opening
   * the window, the popup-blocker outcome). A false positive here just means
   * the copy said "opens Gmail directly" and the send fell through to the
   * ordinary Outbox path instead — the documented, narrow edge case noted in
   * `submit()` itself — never the other way around.
   */
  const singleRecipientBypass = useMemo(() => {
    if (lane !== 'reviewed' || audience.recipients.length !== 1 || !subject || !body) return false
    const only = audience.recipients[0]
    const url = gmailComposeUrl({
      to: only.email,
      subject: render(subject, only.agent).text,
      body: render(body, only.agent).text,
    })
    return !gmailLinkTooLong(url)
  }, [lane, audience, subject, body])

  const cadenceRule = useMemo(() => buildCadence(cadenceState), [cadenceState])
  const cadenceError = useMemo(() => validateCadence(cadenceRule), [cadenceRule])
  const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/
  const fireTimeError = TIME_RE.test(fireTime) ? null : 'Enter a time as HH:MM, like 09:00.'
  const cadenceReady = !cadenceError && !fireTimeError

  /**
   * Templates whose key starts with an underscore are guidance for whoever is
   * writing — voice notes, belated wording — not sendable content. A picker
   * that lists everything eventually puts a coaching note in front of 386
   * agents, so they are filtered here AND `isGuidanceTemplate` is the check.
   *
   * A template carrying a fill-in is also hidden from a blast: offering one she
   * cannot use, then refusing it, is worse than not offering it.
   */
  const realTemplates = templates
    .filter((t) => !isGuidanceTemplate(t.key))
    /**
     * An empty template is not a template yet.
     *
     * Creating one on the Templates tab writes an empty subject and body and
     * leaves her to fill them in. Until she does, it passes every other filter
     * here and sits in the picker looking finished — and picking it replaces
     * whatever she has written with nothing, no confirmation, no undo.
     */
    .filter((t) => t.subject.trim() !== '' || t.body.trim() !== '')

  const usable = realTemplates.filter(
    (t) => lane === 'reviewed' || mergeFieldsUsed(`${t.subject}\n${t.body}`).length === 0,
  )

  /**
   * Walkthrough-flagged 2026-08-23: `usable` going to zero for a blast used to
   * mean the whole "Start from a template" block just never rendered, with
   * nothing on screen saying why — Chris hit this composing a manual blast
   * and every one of the six real templates uses a merge field like
   * `{{first_name}}`, which a message to everybody can't fill in.
   *
   * Only true for a blast, and only when it's the merge-field filter doing
   * the hiding — `realTemplates.length > 0` is what tells the two empty
   * cases apart. Reading lane never reaches this: `usable` there is just
   * `realTemplates`, so if there is nothing to show it is because there are
   * genuinely no templates yet, which the picker's own absence already says
   * plainly enough without another line under it.
   */
  const noBlastableTemplates = lane === 'blast' && usable.length === 0 && realTemplates.length > 0

  const minutes = Math.round(audience.recipients.length * 1.0)
  const hours = (minutes / 60).toFixed(minutes >= 60 ? 1 : 0)

  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  /** Put a merge field where the cursor is. Shared with the template editor. */
  const dropField = (fieldKey: string) => {
    const el = bodyRef.current
    // Only a focused field has a cursor — an untouched one reports 0, which
    // would put every fill-in at the very start. See `insertAtCursor`.
    const live = !!el && document.activeElement === el
    const { text, caret } = insertAtCursor(
      body,
      live ? el.selectionStart : null,
      live ? el.selectionEnd : null,
      `{{${fieldKey}}}`,
    )
    setBody(text)
    // After the repaint, or the next press inserts before this one.
    window.requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(caret, caret)
    })
  }

  /**
   * Cancel leaves the composer AND empties the mailing list.
   *
   * It used to only navigate. The ticks survived, which is right everywhere
   * else — the list is deliberately built across filter changes and screens —
   * but wrong here, because Cancel reads as "forget this". Chris hit it on the
   * first walkthrough: cancelled a single-agent send, landed back on the roster
   * and read the leftover "Mailing list · 1" as an email still sitting in a
   * queue. The real hazard is quieter than the confusion: cancel, walk away,
   * come back, tick four more people and send to five.
   *
   * `clearSelection` records an undo, so a forty-person list built over two
   * passes of filtering is one click from coming back rather than gone. **Put
   * back N** sits above the roster in `Workspace`'s `ListHead` — in plain
   * sight, which is the copy that matters here; the other one, inside the
   * Mailing list menu, is behind a dropdown she has no reason to open. That bar
   * used to disappear on an empty roster and now stands for the undo alone,
   * because Cancel can perfectly well land her on a roster showing nobody.
   *
   * It is a no-op when nothing is ticked, which is the common case: Email
   * pressed with no selection writes to whatever the filters show, so there is
   * no list to empty and no unrelated undo to clobber.
   *
   * `picked` was frozen at mount, so clearing here cannot move the audience
   * out from under a count she has already been shown.
   */
  const cancel = () => {
    clearSelection()
    nav('/')
  }

  const insert = (key: string) => {
    const t = templates.find((x) => x.key === key)
    if (!t) return
    setSubject(t.subject)
    setBody(t.body)
    if (!label.trim()) setLabel(t.name)
  }

  /**
   * The list has done its job — empty it, but only when nothing went wrong.
   *
   * Cancel clears; a send that worked has to clear too, or the two exits mean
   * opposite things and the safer-sounding one is the one that leaves live
   * ticks behind. It is also the worse hazard of the two, because here the
   * emails are real: queue 40, land on the Outbox with 40 still ticked, come
   * back tomorrow, add four more and send again. The occurrence key is the
   * day, so tomorrow is a new key and those 40 are 40 fresh cards with nothing
   * on the Done screen to report — the guard does not reach across a night.
   *
   * **Not called when anything failed.** A partial write is the one moment the
   * audience is worth keeping, because pressing the button again is now the
   * right thing to do: the occurrence key is the day, so the retry finds the
   * 300 that landed, inserts only the 485 that are missing, and says so. That
   * was not true this morning — the key carried seconds, every press was a new
   * job, and a retry duplicated everything that had already saved. Keeping the
   * list would have been useless then and is the whole point now.
   *
   * The undo is recorded as usual — though on a clean reviewed send she is
   * navigated to the Outbox, and "Put back N" lives above the roster, so it is
   * a tab away rather than in front of her.
   */
  const spendList = () => clearSelection('sent')

  /**
   * The moment the FIRST attempt used, reused by every retry on this screen.
   *
   * The occurrence key is the day, and `new Date()` per press meant a retry at
   * 00:00:05 after a failure at 23:59:55 landed on the next day — a different
   * key, colliding with nothing, duplicating every row that had already saved.
   * Exactly the failure the day key exists to prevent, once a night.
   *
   * A ref rather than state: it must not cause a render, and it must be read
   * and written inside the same synchronous run as the send.
   */
  const firstAttempt = useRef<Date | null>(null)

  const submit = async () => {
    if (!lane || !ready) return
    if (repeat && !cadenceReady) return
    setBusy(true)
    setFailed(null)
    const now = firstAttempt.current ?? new Date()
    firstAttempt.current = now
    const name = label.trim() || subject.trim()
    try {
      if (repeat) {
        /**
         * A recurring campaign is a TEMPLATE row, not a blast — no
         * `manual-` id, no `queueBlast`, nothing sent or held anywhere
         * today. `generate-scheduled.ts` is what turns this into an actual
         * blast on each firing, by the same construction `buildBlast` uses.
         *
         * `sched-<slug>` has no date in it, unlike a one-off's
         * `manual-<date>-<slug>` — two recurring campaigns from the same
         * name on the same day are not a real scenario the way two blasts
         * are, so a collision here means the name is already taken, not
         * that this is a safe-to-retry duplicate. Caught below and given
         * its own message rather than the blast lane's "left alone" one,
         * which would be misleading here.
         */
        const id = `sched-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40) || 'send'}`
        const { error: err } = await supabase.from('campaigns').insert({
          id,
          name,
          subject,
          body,
          cadence: cadenceRule,
          audience_rule: { tab: fromTab, filters, search, includeDeparted },
          meeting_time: fireTime,
          is_enabled: true,
          ...(appUser?.id ? { created_by: appUser.id } : {}),
        })
        if (err) {
          // Same check `outbox.ts` uses for a primary-key collision — the
          // Postgres code when it's there, a message match when it isn't,
          // since supabase-js does not always surface `.code` the same way.
          const isDuplicate = err.code === '23505' || /duplicate key|unique constraint/i.test(err.message)
          setFailed(
            isDuplicate
              ? `A recurring campaign called "${name}" already exists. Change the name — under "What is this for?" — and try again.`
              : err.message,
          )
        } else {
          setDone(
            `Saved. ${cadenceSummary(cadenceRule)} at ${fireTime}, it will generate a fresh send for ` +
              `whoever matches ${TAB_LABEL[fromTab]} at the time, and it'll wait in the Outbox for you to ` +
              `review and send. Find it any time on the Scheduled tab — pause or delete it there.`,
          )
        }
      } else if (lane === 'reviewed') {
        /**
         * SINGLE-RECIPIENT SHORTCUT — open Gmail directly instead of parking
         * the one card in the Outbox where it "gets lost with whatever else is
         * in there" (Chris, 2026-08-23 walkthrough). Scope: reviewed lane,
         * exactly one recipient. Bulk/blast is unaffected — "keep bulk as is".
         * See `manual-send-outbox-bypass-flagged-2026-08-23.md`.
         *
         * `window.open` runs BEFORE the `await queueReviewed(...)` below, on
         * purpose — an `await` ahead of it breaks the user-gesture chain and a
         * pop-up blocker eats it silently. Same constraint `Outbox.tsx`'s own
         * `hand()` is built around; the URL is rendered here with the same
         * pure `render()` call `queueReviewed` uses internally, so what opens
         * in Gmail and what gets written to the row are identical text.
         *
         * `recheckAtHandoff` is deliberately not run here — this send follows
         * queueing by seconds, and the original scoping note for this
         * shortcut already reasoned through why that check "does not apply at
         * one".
         */
        const only = audience.recipients.length === 1 ? audience.recipients[0] : null
        const bypassOccurrence = only ? batchKey(subject, now) : null
        let bypassWindow: Window | null = null
        if (only && bypassOccurrence) {
          const url = gmailComposeUrl({
            to: only.email,
            subject: render(subject, only.agent).text,
            body: render(body, only.agent).text,
          })
          // Too long for a Gmail compose URL: no shortcut for this one, it
          // falls through to the ordinary Outbox path below like everybody
          // else whose message hits this same limit there.
          if (!gmailLinkTooLong(url)) {
            // A window named for this exact send, not `_blank` — a second
            // press before the row is confirmed reuses the same tab instead
            // of opening a second Gmail draft of the same message.
            bypassWindow = window.open(url, `gmail-draft-manual-${only.agent.rezen_id}-${bypassOccurrence}`)
            if (bypassWindow) {
              try {
                bypassWindow.opener = null
              } catch {
                // Cross-origin by the time this runs, on some browsers.
              }
            }
            // A blocked pop-up (`bypassWindow` null) is not treated as a
            // shortcut that happened — falls through below, same as `hand()`.
          }
        }

        const r = await queueReviewed({ audience, subject, body, label: name, at: now })
        if (r.error) setFailed(r.error)

        /**
         * `r.id` is populated ONLY when this call created exactly the one row
         * behind the window just opened — see `queueReviewed`'s own comment on
         * `OutboxResult.id`. Anything else (a collision with something already
         * queued today, a write that failed, more than one recipient) falls
         * through to the ordinary message below, on purpose: there is no card
         * to safely wire "It went" to without a second read this shortcut is
         * built to avoid, so it accepts a narrow, documented degraded case
         * there — a Gmail window may already be open with nothing on this
         * screen to confirm it against.
         */
        if (bypassWindow && !r.error && r.created === 1 && r.id !== undefined && only && bypassOccurrence) {
          writeHandedOff(new Set([r.id]))
          setGmailHandoff({ id: r.id, agentId: only.agent.rezen_id, occurrence: bypassOccurrence, to: only.email })
          spendList()
        } else {
          /**
           * This message had never been read by anybody until today.
           *
           * `alreadyQueued` was unreachable while the batch key carried seconds —
           * no two presses could ever collide — so the wording was never tested
           * against a real number, and it showed: "1 were already queued", and a
           * flat "0 emails are waiting in the Outbox" in front of somebody whose
           * whole list was already sitting there. Now that the key is the day,
           * this is the ordinary result of pressing the button twice, and it has
           * to read properly on the first go.
           */
          if (r.created > 0 || !r.error) {
            const already = r.alreadyQueued
            const were = (n: number) => (n === 1 ? 'was' : 'were')
            /**
             * It says "queued earlier today", never "waiting in the Outbox".
             *
             * The uniqueness key spans every status, so a collision can be a card
             * she already sent and cleared: queue 40 at nine, work through all
             * forty, re-send to the five who did not reply at two. All five
             * collide. "Already waiting in the Outbox" sends her to an empty
             * Outbox looking for cards that no longer exist, with the message and
             * the screen flatly contradicting each other.
             */
            setDone(
              r.created === 0 && already > 0
                ? `Nothing new was added — all ${already} had already been queued under this subject earlier today. ` +
                    `If ${already === 1 ? 'it is' : 'they are'} not in the Outbox, ${already === 1 ? 'it has' : 'they have'} already been sent or skipped. ` +
                    `To send this again today, change the subject line.`
                : `${r.created} ${r.created === 1 ? 'email is' : 'emails are'} waiting in the Outbox.` +
                    (already
                      ? ` ${already} ${were(already)} already queued under this subject earlier today, so ${already === 1 ? 'it was' : 'they were'} not queued twice.`
                      : ''),
            )
          }
          /**
           * Straight to the Outbox when there is nothing to read, and only then.
           *
           * The Done screen exists for three messages that live nowhere else:
           * "N were already queued from an earlier go", a partial failure where
           * some rows landed and some did not, and — on the blast side — "that
           * same send was already saved earlier today, so this one was left
           * alone", which means nothing happened at all.
           *
           * Navigating past any of those would drop the message on the floor and
           * leave a count on the next screen that she has no way to reconcile.
           * Carrying it across as a banner on the destination is the tempting
           * alternative and it can be missed, dismissed or lost to a refresh —
           * which takes the whole message with it.
           *
           * So: skip the screen when it has nothing to say, keep it when it does.
           * The extra click now only appears when there is a reason to read.
           */
          if (!r.error) spendList()
          if (!r.error && r.alreadyQueued === 0 && r.created > 0) nav('/outbox')
        }
      } else {
        const day = localDate(now)
        const id = `manual-${day}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30) || 'send'}`
        const r = await queueBlast({
          audience,
          campaignId: id,
          name,
          subject,
          body,
          // The people, by name, always. See `blastAudienceRule` — it lives in
          // `campaigns.ts` so that a test can hold it to that.
          audienceRule: blastAudienceRule(
            audience,
            fromMailingList ? 'mailing list' : `filters · ${fromTab}`,
            now,
          ),
          createdBy: appUser?.id ?? null,
        })
        if (r.error) setFailed(r.error)
        else {
          /**
           * `r.created`, not "no error". The blast lane has two silent
           * do-nothing branches — a campaign with this id already holds an
           * identical subject and body, or the insert lost the primary-key
           * race — and both return no error and no rows.
           *
           * `campaignId` is date plus label with no time in it, so the first
           * branch is easy to reach on purpose: send to 40 at 9am, put the list
           * back, add the 5 who were missed, send the same message again at
           * 11am. Nothing is written for the 5, the stored campaign still holds
           * the original 40 — and on `!r.error` this would have emptied the
           * list of 45 she needs in order to fix it, one line under a message
           * saying the send "was left alone".
           */
          if (r.created) spendList()
          setDone(
            r.created
              ? `Saved for ${audience.recipients.length} ${audience.recipients.length === 1 ? 'agent' : 'agents'}. ` +
                  'Nothing has been sent — it is being held on the Bulk tab until you open it there, ' +
                  'send yourself a test, and confirm the number.'
              : 'That same send was already saved earlier today, so this one was left alone.',
          )
        }
        /**
         * The blast lane keeps its screen even on success.
         *
         * Its message is not a receipt, it is an instruction — the send is
         * *held*, and it goes nowhere until she opens the Bulk tab, sends
         * herself a test and types the number. Dropping her on that tab without
         * having read why would be the one place a silent navigation could
         * plausibly end in an unintended send.
         *
         * `r.created` false means the opposite of success: nothing was saved
         * because the same send already existed today.
         */
      }
    } catch (err) {
      console.error('queueing failed', err)
      setFailed('That did not go through. Nothing was queued — try again.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Who is being left out — and only who is being left out.
   *
   * `missing-merge` used to be printed in this list, under the heading "Not
   * included". Those agents *are* included; they are getting the email with
   * `{{first_name}}` still written in it, which is a different and worse
   * problem than not getting it. Two facts under one heading, one of them
   * false. They are now separated, and the merge one is stated as what it is.
   */
  const dropped = audience.problems.filter((p) => p.kind !== 'missing-merge')
  const blanks = audience.problems.filter((p) => p.kind === 'missing-merge')

  const Excluded = () =>
    dropped.length === 0 ? null : (
      <ul className="hint" style={{ margin: '6px 0 0 16px' }}>
        {dropped.map((p, i) => (
          <li key={i}>
            {p.kind === 'no-email' && `${p.agents.length} have no usable email address`}
            {p.kind === 'departed' && `${p.agents.length} have left the brokerage`}
            {p.kind === 'suppressed' && `${p.agents.length} are tagged "${p.tag}"`}
          </li>
        ))}
      </ul>
    )

  const Blanks = () =>
    blanks.length === 0 ? null : (
      <>
        <p className="hint" style={{ marginTop: 8 }}>
          These <strong>are</strong> getting it, but a fill-in will come through blank for them:
        </p>
        <ul className="hint" style={{ margin: '4px 0 0 16px' }}>
          {blanks.map((p, i) => (
            <li key={i}>
              {p.agents.length} have no {p.kind === 'missing-merge' ? p.field.replace(/_/g, ' ') : ''}
              {p.agents.length <= 4 && p.kind === 'missing-merge'
                ? ` — ${p.agents.map((a) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim() || a.rezen_id).join(', ')}`
                : ''}
            </li>
          ))}
        </ul>
      </>
    )

  if (gmailHandoff) {
    /**
     * Marks the one row `queueReviewed` created — not a re-fetch, the exact
     * id, agent and occurrence recorded when the Gmail window opened above.
     * The `facts` shape matches what `queueReviewed` itself wrote onto that
     * row (`lane`, `from`, `reply_to`, `to` — see its own insert), so this
     * write is not overwriting anything it did not already know.
     */
    const markSent = async () => {
      setHandoffBusy(true)
      setHandoffError(null)
      const err = await markDraft(
        { id: gmailHandoff.id, agent_id: gmailHandoff.agentId, trigger: MANUAL_TRIGGER, occurrence: gmailHandoff.occurrence },
        'sent',
        { lane: 'reviewed' as Lane, from: LANE_FROM.reviewed, reply_to: REPLY_TO, to: gmailHandoff.to },
        appUser?.id ?? null,
      )
      setHandoffBusy(false)
      if (err) return setHandoffError(err)
      setHandoffDone(true)
    }

    return (
      <div className="board tpl">
        <section className="sec">
          <h3>Gmail opened</h3>
          <p className="cnt">
            {handoffDone
              ? 'Marked as sent.'
              : 'A Gmail draft opened in another tab, already addressed and filled in. Press Send there, then come back and press "It went".'}
          </p>
          {handoffError && <p className="error">{handoffError}</p>}
          <div className="savebar">
            {!handoffDone && (
              <button className="btn" disabled={handoffBusy} onClick={() => void markSent()}>
                {handoffBusy ? 'Saving…' : 'It went'}
              </button>
            )}
            <button className="btn ghost" onClick={() => nav('/')}>Back to the roster</button>
          </div>
        </section>
      </div>
    )
  }

  if (done) {
    return (
      <div className="board tpl">
        <section className="sec">
          <h3>Done</h3>
          <p className="cnt">{done}</p>
          {failed && <p className="error">{failed}</p>}
          {/*
            The retry lives HERE, not only on the confirm screen.

            A partial write sets `done` as well as `failed` — 300 of 785 landed,
            so there is a count worth reading — and this screen replaces the one
            with the button on it. The whole point of keying the batch on the
            day is that pressing again fills the gap, and until now the only way
            back to a button was: roster, re-tick, re-open the composer, and
            retype the subject character for character. Anything less than an
            exact match is a different key and queues all 785 again.

            Same `submit`, so the frozen `firstAttempt` and the same subject
            give the same occurrence, and the rows that landed are recognised.
          */}
          <div className="savebar">
            {failed && !repeat && (
              <button className="btn" disabled={busy} onClick={() => void submit()}>
                {busy ? 'Trying again…' : 'Try the rest again'}
              </button>
            )}
            {repeat ? (
              <button className={failed ? 'btn ghost' : 'btn'} onClick={() => nav('/scheduled')}>
                Go to Scheduled
              </button>
            ) : (
              <button className={failed ? 'btn ghost' : 'btn'} onClick={() => nav('/outbox')}>
                Go to the Outbox
              </button>
            )}
            <button className="btn ghost" onClick={() => nav('/')}>Back to the roster</button>
          </div>
          {failed && (
            <p className="hint" style={{ marginTop: 8 }}>
              Pressing this again is safe — the ones that already saved are recognised and left
              alone, so you get the missing ones and nothing else.
            </p>
          )}
        </section>
      </div>
    )
  }

  return (
    <div className="board tpl">
      <section className="sec">
        <h3>
          Email
          <span className="pill s-drafted">
            {step === 'how'
              ? 'who and how'
              : step === 'what'
                ? 'write it'
                : step === 'cadence'
                  ? 'schedule it'
                  : 'confirm'}
          </span>
        </h3>

        {/*
          The one thing that stops this screen dead. Tags are where "do not
          email me" is recorded; without them, every guard on this page is
          switched off and looks exactly like a roster where nobody asked.
        */}
        {tagsError && (
          <>
            <p className="error">
              The agent tags did not load, so this cannot tell who has asked not to be emailed.
              Nothing can be queued until they do. {tagsError}
            </p>
            <div className="savebar">
              <button className="btn" disabled={tagsLoading} onClick={() => reloadTags()}>
                {tagsLoading ? 'Trying…' : 'Try loading the tags again'}
              </button>
            </div>
          </>
        )}

        {/* ------------------------------------------------------------ HOW */}
        {/*
          WHO used to be a screen of its own and is now the heading of this one.

          Everything it showed was display — a count, a line of provenance, the
          automatic exclusions, and a sentence explaining that the suppression
          tags resolve once the lane is known. Its only input was a Continue
          button, and this screen has no Continue of its own because each lane
          button chooses and advances in one click. So the two merged with
          nothing lost and one click saved.

          The order of numbers she sees is unchanged: the count here is still
          pre-lane, the exclusions still resolve after she picks, and the
          confirm screen still says both again. That sequence is what the
          comment on `audience` above is protecting, and merging two screens
          does not touch it.
        */}
        {step === 'how' && (
          <>
            <p className="cnt">
              {audience.recipients.length} {audience.recipients.length === 1 ? 'agent' : 'agents'}
            </p>
            <p className="hint">
              {fromMailingList
                ? 'From your mailing list.'
                : `Everyone the ${TAB_LABEL[fromTab]} list is currently showing. Tick people first if you want a smaller group.`}
              {audience.asked !== audience.recipients.length &&
                ` ${audience.asked - audience.recipients.length} of the ${audience.asked} cannot be emailed:`}
            </p>
            <Excluded />

            <p className="cnt" style={{ marginTop: 14 }}>
              Do you want to read and send each one yourself, or send everybody the same message?
            </p>
            <p className="hint">
              Reading {audience.recipients.length} individually is roughly {minutes >= 60 ? `${hours} hours` : `${minutes} minutes`}.
            </p>
            {/*
              The guard that was on Continue now sits on both lane buttons —
              there is no longer an earlier screen to have stopped her.
            */}
            <div className="savebar">
              <button
                className="btn"
                disabled={!audience.recipients.length || !!tagsError}
                onClick={() => { setLane('reviewed'); setStep('what') }}
              >
                Read and send each one
              </button>
              <button
                className="btn"
                disabled={!audience.recipients.length || !!tagsError}
                onClick={() => { setLane('blast'); setRepeat(false); setStep('what') }}
              >
                Same message to everybody
              </button>
              {/*
                Only offered off a filter/tab, never a hand-ticked mailing
                list. A recurring campaign's audience has to be re-resolved
                fresh at every firing, and a ticked list is a frozen snapshot
                — see the note on `repeat` above.
              */}
              {!fromMailingList && (
                <button
                  className="btn ghost"
                  disabled={!audience.recipients.length || !!tagsError}
                  onClick={() => { setLane('blast'); setRepeat(true); setStep('what') }}
                >
                  Repeat this on a schedule
                </button>
              )}
            </div>
            <p className="hint" style={{ marginTop: 10 }}>
              Reading each one goes out through your Real Broker Gmail and lands in your Sent
              folder, and you can drop names into it. The same message to everybody goes out in
              one go and cannot use names — nobody reads those before they leave.
              {!fromMailingList && (
                <>
                  {' '}"Repeat this on a schedule" saves it as a recurring campaign instead — each
                  time it's due, a new one of these is generated fresh for whoever currently
                  matches {TAB_LABEL[fromTab]}, waiting in the Outbox for you to review and send.
                </>
              )}
            </p>
            {/*
              Said after the choice rather than before it, because some agents
              are tagged out of one kind of email and not the other, so the
              number above cannot be final until she has picked. A number that
              changes without warning is worse than one that says it might.
            */}
            <p className="hint">
              Anyone tagged "no outreach" or "no bulk emails" comes out once you have chosen
              which of the two this is.
            </p>
            {/*
              Cancel is on every step, not only the first.

              No email has been written down at any of these steps; the first
              write is "Yes, queue it". Leaving costs what she had typed and
              the mailing list, and the list comes back with one press of
              "Put back N" on the roster — see `cancel` above.
            */}
            <div className="savebar">
              <button className="btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
            </div>
          </>
        )}

        {/* ----------------------------------------------------------- WHAT */}
        {step === 'what' && lane && (
          <>
            <p className="hint">
              From <strong>{LANE_FROM[lane]}</strong>
              {lane === 'blast' && <> · replies go to {REPLY_TO}</>}
            </p>

            {usable.length > 0 && (
              <>
                <div className="hint" style={{ marginTop: 10, marginBottom: 4 }}>Start from a template</div>
                <select className="areasel-d" defaultValue="" onChange={(e) => insert(e.target.value)}>
                  <option value="">Blank page</option>
                  {usable.map((t) => (
                    <option key={t.key} value={t.key}>{t.name}</option>
                  ))}
                </select>
              </>
            )}

            {/*
              Says why the picker above is missing rather than just leaving it
              out — decided over show-and-strip-fields and show-and-warn, both
              of which would offer a template a blast literally cannot send.
            */}
            {noBlastableTemplates && (
              <p className="hint" style={{ marginTop: 10, marginBottom: 4 }}>
                No templates without personalization yet — every one uses a name or another
                per-agent field, which a message to everybody can't fill in. Read and send each
                one individually to use them, or write this one from a blank page below.
              </p>
            )}

            <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>What is this for?</div>
            <input
              className="areasel-d"
              value={label}
              placeholder="e.g. 2027 licence renewals"
              onChange={(e) => setLabel(e.target.value)}
            />

            <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Subject</div>
            <input className="areasel-d" value={subject} onChange={(e) => setSubject(e.target.value)} />

            <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Message</div>
            <textarea
              ref={bodyRef}
              rows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />

            {lane === 'reviewed' && (
              <p className="hint">
                {/*
                  Inserted where the cursor is, not appended to the end. The
                  same helper the template editor uses — a name belongs in the
                  middle of a sentence, and having to cut and paste it there
                  defeats the button.
                */}
                Drop in, where the cursor is:{' '}
                {MERGE_FIELDS.map((f) => (
                  <button
                    key={f.key}
                    className="btn ghost sm"
                    style={{ marginRight: 4 }}
                    onClick={() => dropField(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </p>
            )}

            {problems.map((p, i) => (
              <p key={i} className={p.severity === 'stop' ? 'error' : 'hint'}>{p.message}</p>
            ))}

            {lane === 'reviewed' && audience.recipients[0] && subject && body && (
              <>
                <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>
                  How it reads for {audience.recipients[0].agent.first_name} {audience.recipients[0].agent.last_name}
                </div>
                <div className="dbody">
                  <strong>{render(subject, audience.recipients[0].agent).text}</strong>
                  {'\n\n'}
                  {render(body, audience.recipients[0].agent).text}
                </div>
              </>
            )}

            <div className="savebar">
              <button
                className="btn"
                disabled={!ready || busy}
                onClick={() => setStep(repeat ? 'cadence' : 'confirm')}
              >
                Continue
              </button>
              {/*
                Back un-picks the lane, and that is load-bearing rather than tidy.

                `audience` is recomputed with the chosen lane's suppression, and
                the guard on both lane buttons reads its count. Leave `lane` set
                on the way back and an agent tagged `no-bulk-email` gives you a
                screen offering two buttons, both disabled, for a person the
                *other* lane could have emailed perfectly well — with no
                Continue and no way out but Cancel, which throws away whatever
                she had typed.

                It also makes the count on that screen honest again: it is
                described as pre-lane, and it only is if arriving there clears
                the lane.
              */}
              <button
                className="btn ghost"
                disabled={busy}
                onClick={() => { setLane(null); setRepeat(false); setStep('how') }}
              >
                Back
              </button>
              <button className="btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
            </div>
          </>
        )}

        {/* --------------------------------------------------------- CADENCE */}
        {/*
          Only reached with `lane === 'blast' && repeat`. How often, and what
          time of day — see `cadence-picker.ts` for the mapping from this
          state to a real `CadenceRule`, and `cadence.ts` for what each type
          actually means and how it is validated.
        */}
        {step === 'cadence' && lane && (
          <>
            <p className="cnt">How often should this go out?</p>

            <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Cadence</div>
            <select
              className="areasel-d"
              value={cadenceState.type}
              onChange={(e) => setCadenceState((s) => ({ ...s, type: e.target.value as CadenceType }))}
            >
              <option value="weekly">Every week</option>
              <option value="every_n_weeks">Every few weeks</option>
              <option value="monthly_date">Monthly, on a date</option>
              <option value="monthly_nth_weekday">Monthly, on a weekday</option>
            </select>

            {(cadenceState.type === 'weekly' || cadenceState.type === 'every_n_weeks' || cadenceState.type === 'monthly_nth_weekday') && (
              <>
                <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Day</div>
                <select
                  className="areasel-d"
                  value={cadenceState.dayOfWeek}
                  onChange={(e) => setCadenceState((s) => ({ ...s, dayOfWeek: Number(e.target.value) as CadencePickerState['dayOfWeek'] }))}
                >
                  {DAY_NAMES.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </>
            )}

            {cadenceState.type === 'every_n_weeks' && (
              <>
                <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Every how many weeks</div>
                <input
                  className="areasel-d"
                  type="number"
                  min={2}
                  value={cadenceState.everyN}
                  style={{ maxWidth: 120 }}
                  onChange={(e) => setCadenceState((s) => ({ ...s, everyN: Math.max(1, Number(e.target.value) || 1) }))}
                />
              </>
            )}

            {cadenceState.type === 'monthly_date' && (
              <>
                <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Day of the month</div>
                <input
                  className="areasel-d"
                  type="number"
                  min={1}
                  max={31}
                  value={cadenceState.monthlyDate}
                  style={{ maxWidth: 120 }}
                  onChange={(e) => setCadenceState((s) => ({ ...s, monthlyDate: Math.min(31, Math.max(1, Number(e.target.value) || 1)) }))}
                />
                <p className="hint">Past the end of a shorter month, this fires on that month's last day instead.</p>
              </>
            )}

            {cadenceState.type === 'monthly_nth_weekday' && (
              <>
                <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>Which week(s)</div>
                {WEEK_LABELS.map((w) => (
                  <label key={w.value} className="chk" style={{ marginRight: 12 }}>
                    <input
                      type="checkbox"
                      checked={cadenceState.nthWeeks.includes(w.value)}
                      onChange={(e) =>
                        setCadenceState((s) => ({
                          ...s,
                          nthWeeks: e.target.checked
                            ? [...s.nthWeeks, w.value]
                            : s.nthWeeks.filter((x) => x !== w.value),
                        }))
                      }
                    />
                    {w.label}
                  </label>
                ))}
                {cadenceState.nthWeeks.length === 0 && (
                  <p className="error">Pick at least one week.</p>
                )}
              </>
            )}

            <div className="hint" style={{ marginTop: 12, marginBottom: 4 }}>What time (America/New York)</div>
            <input
              className="areasel-d"
              value={fireTime}
              placeholder="09:00"
              style={{ maxWidth: 120 }}
              onChange={(e) => setFireTime(e.target.value.trim())}
            />
            {fireTimeError && <p className="error">{fireTimeError}</p>}

            {!cadenceError && !fireTimeError && (
              <p className="hint" style={{ marginTop: 10 }}>
                {cadenceSummary(cadenceRule)} at {fireTime}. On the day, it's generated and
                waiting in the Outbox for you to review and send — nothing goes out by itself.
              </p>
            )}
            {cadenceError && <p className="error">{cadenceError}</p>}

            <div className="savebar" style={{ marginTop: 12 }}>
              <button className="btn" disabled={!cadenceReady || busy} onClick={() => setStep('confirm')}>
                Continue
              </button>
              <button className="btn ghost" disabled={busy} onClick={() => setStep('what')}>Back</button>
              <button className="btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
            </div>
          </>
        )}

        {/* -------------------------------------------------------- CONFIRM */}
        {step === 'confirm' && lane && (
          <>
            <p className="cnt">
              {repeat
                ? `Save "${label.trim() || subject.trim()}" as a recurring campaign — ${cadenceSummary(cadenceRule)} at ${fireTime}?`
                : lane === 'reviewed'
                  ? singleRecipientBypass
                    ? 'Open this in Gmail, ready to send?'
                    : `Put ${audience.recipients.length} ${audience.recipients.length === 1 ? 'email' : 'emails'} in the Outbox for you to read and send?`
                  : `Queue one message to ${audience.recipients.length} ${audience.recipients.length === 1 ? 'agent' : 'agents'}?`}
            </p>
            <p className="hint">
              From {LANE_FROM[lane]}. Subject: <strong>{subject}</strong>
            </p>
            {repeat && (
              <p className="hint">
                Sends to whoever matches {TAB_LABEL[fromTab]} at the time — currently{' '}
                {audience.recipients.length} {audience.recipients.length === 1 ? 'agent' : 'agents'}, but that
                is re-checked fresh on every firing, not frozen today.
              </p>
            )}
            {dropped.length > 0 && (
              <>
                <p className="hint">Not included:</p>
                <Excluded />
              </>
            )}
            <Blanks />
            <p className="hint">
              {repeat
                ? "Nothing sends today. This creates the standing rule, on the Scheduled tab. Each time it's due, " +
                  "a fresh one is generated and waits in the Outbox for you to review and send — it never sends by itself."
                : lane === 'reviewed'
                  ? singleRecipientBypass
                    ? 'Nothing sends now. Gmail opens with it already addressed and filled in — you press Send there.'
                    : 'Nothing sends now. Each one opens in Gmail when you press Send on its card.'
                  : 'Nothing sends now. It is saved and held on the Bulk tab, and only goes when you open it there and confirm the number.'}
            </p>
            {failed && <p className="error">{failed}</p>}
            <div className="savebar">
              {/*
                `ready` is checked again here, not just on the previous screen.
                The roster keeps loading in the background — tags can arrive, or
                fail to — and the last gate before a write should be the one
                that reads the current answer.
              */}
              <button className="btn" disabled={busy || !ready || (repeat && !cadenceReady)} onClick={() => void submit()}>
                {busy
                  ? 'Saving…'
                  : repeat
                    ? 'Yes, save the recurring campaign'
                    : singleRecipientBypass
                      ? 'Yes, open it in Gmail'
                      : 'Yes, queue it'}
              </button>
              {/*
                Back and Cancel are both disabled mid-write, and so are the ones
                on the two steps behind this.

                `queueReviewed` chunks 785 rows and takes seconds. With only
                this screen's Cancel guarded, Back was still live: back to WHAT,
                Cancel there, and the component unmounts with the insert still
                in flight. An SPA navigation does not abort it, so the rows land
                anyway — and `done` and `failed` are rendered only here and on
                the done screen, so she ends up on the roster with no count and,
                if a chunk failed at row 300, no error either. Guarding one of
                three exits guards nothing.
              */}
              <button className="btn ghost" disabled={busy} onClick={() => setStep(repeat ? 'cadence' : 'what')}>
                Back
              </button>
              <button className="btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
